import { Device } from "@fairfleet/geotab";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { uniq, uniqWith, flatten } from "lodash";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { chunkArray } from "@/utilities/chunkArray";

const DEFAULT_DEVICE_FIELDS = ["id", "name", "serialNumber"];

/**
 * Returns the devices with the given ids.
 *
 * @param ids - The ids of the devices.
 * @returns - The array of {@link Device}.
 */
export function useDevicesByIds(ids?: string[], fields = DEFAULT_DEVICE_FIELDS) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const uniqueIds = ids ? uniq(ids) : [];

  const queryClient = useQueryClient();

  return useQuery<Device[]>({
    queryKey: ["devices", activeSessionId, uniqueIds, fields],

    // The ids are part of the key, so a caller whose set of ids moves — the map, as it is panned —
    // starts a query with no data of its own and would otherwise show nothing until it returns.
    placeholderData: keepPreviousData,

    queryFn: async ({ signal }) => {
      if (uniqueIds.length === 0) {
        return [];
      }

      const idChunks = chunkArray(uniqueIds, 5000);
      const devicePromises = idChunks.map(idChunk =>
        geotabClient.get(
          "Device",
          {
            deviceIds: idChunk,
            propertySelector: {
              fields,
            },
          },
          10000,
          signal,
        ),
      );

      const devicesArrays = await Promise.all(devicePromises);
      const devices = uniqWith(flatten(devicesArrays), (a, b) => a.id === b.id);

      devices.forEach(device => {
        queryClient.setQueryData(["device", activeSessionId, device.id], device);
      });

      return devices;
    },
  });
}
